import { useCallback, useEffect, useRef, useState } from "react";

const ESPN_FEEDS = [
  {
    league: "NBA",
    color: "#E52324",
    url: "https://site.api.espn.com/apis/site/v2/sports/basketball/nba/scoreboard",
  },
  {
    league: "MLB",
    color: "#002D72",
    url: "https://site.api.espn.com/apis/site/v2/sports/baseball/mlb/scoreboard",
  },
  {
    league: "NHL",
    color: "#000000",
    url: "https://site.api.espn.com/apis/site/v2/sports/hockey/nhl/scoreboard",
  },
  {
    league: "MLS",
    color: "#78BE20",
    url: "https://site.api.espn.com/apis/site/v2/sports/soccer/usa.1/scoreboard",
  },
];

const fallbackItems = [
  {
    id: "fb1",
    league: "NBA",
    leagueColor: "#E52324",
    away: "BOS",
    home: "PHI",
    status: "Loading scores…",
  },
  {
    id: "fb2",
    league: "MLB",
    leagueColor: "#002D72",
    away: "LAD",
    home: "SF",
    status: "Loading scores…",
  },
  {
    id: "fb3",
    league: "NHL",
    leagueColor: "#000000",
    away: "FLA",
    home: "TBL",
    status: "Loading scores…",
  },
];

function parseEvent(event, league, color, idx) {
  try {
    const comp = event?.competitions?.[0];
    if (!comp) return null;

    const competitors = comp.competitors ?? [];
    const away =
      competitors.find((teamItem) => teamItem.homeAway === "away") ??
      competitors[0];
    const home =
      competitors.find((teamItem) => teamItem.homeAway === "home") ??
      competitors[1];
    if (!away || !home) return null;

    const awayAbbr =
      away.team?.abbreviation ?? away.team?.shortDisplayName ?? "???";
    const homeAbbr =
      home.team?.abbreviation ?? home.team?.shortDisplayName ?? "???";

    const statusType = comp.status?.type?.name ?? "";
    const shortDetail =
      comp.status?.type?.shortDetail ?? comp.status?.displayClock ?? "";

    const isLive =
      statusType === "STATUS_IN_PROGRESS" || statusType === "STATUS_HALFTIME";
    const isFinal =
      statusType.includes("FINAL") || statusType === "STATUS_FINAL";

    let statusLabel = shortDetail;
    if (statusType === "STATUS_HALFTIME") statusLabel = "HALFTIME";
    if (isFinal) statusLabel = "FINAL";

    const awayScore =
      isFinal || isLive ? parseInt(away.score ?? "", 10) : undefined;
    const homeScore =
      isFinal || isLive ? parseInt(home.score ?? "", 10) : undefined;

    return {
      id: `${league}-${event.id ?? idx}`,
      league,
      leagueColor: color,
      away: awayAbbr,
      home: homeAbbr,
      awayScore: Number.isNaN(awayScore) ? undefined : awayScore,
      homeScore: Number.isNaN(homeScore) ? undefined : homeScore,
      status: statusLabel,
      live: isLive,
    };
  } catch {
    return null;
  }
}

async function fetchLeague(feed) {
  const response = await fetch(feed.url, { cache: "no-store" });
  if (!response.ok) throw new Error(`${feed.league} fetch failed`);

  const json = await response.json();
  const events = json?.events ?? [];

  return events
    .map((eventItem, index) =>
      parseEvent(eventItem, feed.league, feed.color, index)
    )
    .filter(Boolean);
}

function TickerCard({ item }) {
  const hasScore =
    item.awayScore !== undefined && item.homeScore !== undefined;

  return (
    <div className="inline-flex items-center gap-2.5 px-3 py-1 mr-6 whitespace-nowrap">
      <span
        className="text-[9px] font-black tracking-wider px-1.5 py-0.5 rounded"
        style={{ backgroundColor: item.leagueColor, color: "#fff" }}
      >
        {item.league}
      </span>

      {hasScore ? (
        <div className="flex items-center gap-1.5 text-[11px]">
          <span className="text-white/90 font-black">{item.away}</span>
          <span className="text-white/60">{item.awayScore}</span>
          <span className="text-white/30 mx-0.5">–</span>
          <span className="text-white/60">{item.homeScore}</span>
          <span className="text-white/90 font-black">{item.home}</span>
        </div>
      ) : (
        <div className="flex items-center gap-1.5 text-[11px]">
          <span className="text-white/90 font-black">{item.away}</span>
          <span className="text-white/30">vs</span>
          <span className="text-white/90 font-black">{item.home}</span>
        </div>
      )}

      <span
        className={`text-[9px] font-black uppercase tracking-wider ${
          item.live ? "text-[#00C853]" : "text-white/40"
        }`}
      >
        {item.live && (
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#00C853] mr-1 animate-pulse align-middle" />
        )}
        {item.status}
      </span>

      <span className="text-white/10 ml-2">|</span>
    </div>
  );
}

const SportsTicker = () => {
  const scrollRef = useRef(null);
  const positionRef = useRef(0);
  const animationRef = useRef(0);
  const [items, setItems] = useState(fallbackItems);
  const [hasLive, setHasLive] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const results = await Promise.allSettled(ESPN_FEEDS.map(fetchLeague));
      const merged = [];

      results.forEach((result) => {
        if (result.status === "fulfilled") {
          merged.push(...result.value);
        }
      });

      if (merged.length > 0) {
        setItems(merged);
        setHasLive(merged.some((scoreItem) => scoreItem.live));
      }
    } catch {
      /* keep whatever we have */
    }
  }, []);

  useEffect(() => {
    refresh();
    const intervalId = setInterval(refresh, 60000);
    return () => clearInterval(intervalId);
  }, [refresh]);

  useEffect(() => {
    const element = scrollRef.current;
    if (!element) return;

    const tick = () => {
      positionRef.current += 0.5;
      const halfWidth = element.scrollWidth / 2;

      if (halfWidth > 0 && positionRef.current >= halfWidth) {
        positionRef.current = 0;
      }

      element.style.transform = `translateX(-${positionRef.current}px)`;
      animationRef.current = requestAnimationFrame(tick);
    };

    animationRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animationRef.current);
  }, [items]);

  return (
    <div
      className="bg-[#111] border-b border-white/5 overflow-hidden relative"
      style={{ fontFamily: "'Outfit', 'Inter', sans-serif" }}
    >
      <div className="absolute left-0 top-0 bottom-0 w-12 bg-gradient-to-r from-[#111] to-transparent z-10 pointer-events-none" />
      <div className="absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-[#111] to-transparent z-10 pointer-events-none" />

      <div className="absolute left-3 top-1/2 -translate-y-1/2 z-20 flex items-center gap-1">
        <span
          className={`inline-block w-1.5 h-1.5 rounded-full ${
            hasLive ? "bg-[#00C853]" : "bg-[#E52324]"
          } animate-pulse`}
        />
        <span className="text-[8px] font-black tracking-widest text-[#E52324]/80 uppercase">
          {hasLive ? "LIVE" : "SCORES"}
        </span>
      </div>

      <div className="py-1.5">
        <div ref={scrollRef} className="inline-flex will-change-transform">
          {[...items, ...items].map((item, index) => (
            <TickerCard key={`${item.id}-${index}`} item={item} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default SportsTicker;
